import { Injectable, inject } from '@angular/core';
import { Geometry } from '@turf/turf';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { MapStyle } from '../shared/models/map-configuration';
import { MapState, MAP_STATE } from './models';
import { AppStateService } from '@modules/core/services/app-state.service';
import { PaddingOptions } from 'mapbox-gl';

@Injectable({
    providedIn: 'root'
})
export class MapStateService {
    private readonly _appStateService = inject(AppStateService);

    private readonly state$: Observable<MapState>;
    private readonly _state$: BehaviorSubject<MapState>;
    private _mapState: MapState;
    private _hasInitiated = false;


    constructor() {
        this._state$ = new BehaviorSubject(undefined);
        this.state$ = this._state$.asObservable();
        this.initMapState();
    }

    public init() {
        if (!this._hasInitiated) {
            this.initMapState();
            this._state$.next(this._mapState);
            this._hasInitiated = true;
        }
    }

    public getMapPropertyState<K extends keyof MapState>(propertyKey: K) {
        return this._state$.pipe(
            map(state => state && state[propertyKey]),
            distinctUntilChanged()
        );
    }

    public resetToNetworkState() {
        this.initMapState();
        localStorage.setItem(this._getLocalStorageKey(), JSON.stringify(this._mapState));
        this._state$.next(this._mapState);
    }

    public set loaded(value) {
        this._mapState.loaded = value;
        this._state$.next(this._mapState);
    }

    public get loaded() {
        return this._mapState.loaded;
    }

    public state(): MapState {
        return this._state$.value;
    }

    public get(): MapState {
        return this._mapState;
    }

    public setStyle(style: MapStyle) {
        this._mapState.style = style;
        this.storeMapState();
    }

    public setItemsClicked(items: {
        id: any;
        type: string;
    }[]) {
        this._mapState.itemsClicked = items;
        this._state$.next(this._mapState);
    }

    public setClickedCoordinate(longLat: {
        lng: number; lat: number;
    }) {
        this._mapState.clickedCoordinate = longLat;
        this._state$.next(this._mapState);
    }

    public setLeftClick() {
        this._mapState.leftClicked={};
        this._state$.next(this._mapState);
    }

    public setRightClick(longLat: {
        lng: number; lat: number
    }, point: { x, y }
    ) {
        this._mapState.rightClicked={lat:longLat.lat,lng:longLat.lng,x:point.x,y:point.y};
        this._state$.next(this._mapState);
    }

    public setCenter(center: [number, number]) {
        this._mapState.center = center;
        this.storeMapState();
    }

    public setZoom(zoom: number) {
        this._mapState.zoom = zoom;
        this.storeMapState();
    }

    public setDragEvent(started: boolean) {
        this._mapState.dragging = started;
        this.storeMapState();
    }

    public setPitch(pitch: number) {
        this._mapState.pitch = pitch;
        this.storeMapState();
    }

    public setBearing(bearing: number) {
        this._mapState.bearing = bearing;
        this.storeMapState();
    }

    public setPadding(padding: PaddingOptions) {
        this._mapState.padding = padding;
        this.storeMapState();
    }

    public setBbox(bbox: Geometry, bounds: number[][]) {
        this._mapState.bbox = bbox;
        this._mapState.bounds = bounds;
        this.storeMapState();
    }

    public setMapState(state: MAP_STATE) {
        this._mapState.state = state;
        this.storeMapState();
    }

    public async storePersistedState(): Promise<void> {
        await this._appStateService.setPersistedMapState(this._mapState.center, this._mapState.zoom);
    }

    private storeMapState(): void {
        this._mapState.itemsClicked = [];
        localStorage.setItem(this._getLocalStorageKey(), JSON.stringify(this._mapState));
        this._state$.next(this._mapState);
    }

    private initMapState(): void {
        const localStorageKey = this._getLocalStorageKey();
        const localStorageMapState = localStorage.getItem(localStorageKey);

        const mapPersistedState = this._appStateService.stateSnapshot?.mapPersistedState;

        if (localStorageMapState) {
            const storedMapState = JSON.parse(localStorageMapState);
            storedMapState.loaded = false;
            this._mapState = storedMapState;
        } else {
            this._mapState = new MapState();
            this._mapState.zoom = 12;
            this._mapState.pitch = 0;
            this._mapState.bearing = 0;
            this._mapState.itemsClicked = [];

            const center = this._getCenterLocation();
            if (center) {
                this._mapState.center = center;
            }
        }

        // Override with persisted user settings
        this._mapState.center = mapPersistedState?.center ? mapPersistedState.center : this._mapState.center;
        this._mapState.zoom = mapPersistedState?.zoom ? mapPersistedState.zoom : this._mapState.zoom;

        // always set the state to view
        this._mapState.state = MAP_STATE.VIEW;
    }

    private _getCenterLocation(): [number, number] {
        return [13.019164355557791, 55.59596639521283];
    }

    private _getLocalStorageKey() {
        return 'mapState';
    }
}